import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Card, Field, Input, Stack } from '@dental-staffing-platform/ui';
import { useAuth } from '../auth-context.js';
import { fetchGoogleRedirectConfig } from '../auth-client.js';
import { startGoogleRedirect, type GoogleRedirectConfig } from '../google.js';
import { sanitizeReturnTo } from '../return-to.js';

export interface LoginProps {
  /** Product/app name shown at the top of the form. */
  title?: string;
  subtitle?: string;
  /** Optional one-click demo sign-ins (label + credentials). */
  demoAccounts?: Array<{ label: string; email: string; password: string }>;
  /** Link to the forgot-password screen. */
  forgotPasswordHref?: string;
  /** When true, omit the full-page `<main>` chrome (e.g. nested on invite landing). */
  embedded?: boolean;
  /** Enables Google discovery/rendering only after the host app has configured the provider. */
  googleSignInEnabled?: boolean;
  /** Same-origin destination after Google sign-in. */
  googleReturnTo?: string;
}

/**
 * Login screen: email/password, Google redirect, and a TOTP/MFA step. Presentation only — all auth
 * work is delegated to the auth context. Styled with the shared UI kit (Chairside).
 */
export function Login({
  title = 'Dental Staffing',
  subtitle = 'Sign in to your account',
  demoAccounts,
  forgotPasswordHref = '/forgot-password',
  embedded = false,
  googleSignInEnabled = false,
  googleReturnTo = '/',
}: LoginProps) {
  const { loginWithPassword, verifyMfa, status, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [googleConfig, setGoogleConfig] = useState<GoogleRedirectConfig | null>(null);

  useEffect(() => {
    if (!googleSignInEnabled) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const config = await fetchGoogleRedirectConfig();
        if (controller.signal.aborted || !config) return;
        setGoogleConfig(config);
      } catch {
        // Google is optional; keep the password form visually complete when discovery fails.
      }
    })();
    return () => controller.abort();
  }, [googleSignInEnabled]);

  async function onGoogleClick() {
    if (!googleConfig) return;
    setLocalError(null);
    try {
      await startGoogleRedirect(googleConfig, sanitizeReturnTo(googleReturnTo) ?? '/');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Google sign-in is unavailable.');
    }
  }

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    try {
      const result = await loginWithPassword(email, password);
      if (result === 'mfa_required') {
        setShowMfa(true);
        setPassword('');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setShowMfa(false);
      setLocalError(msg || 'Invalid email or password.');
    }
  }

  async function onMfaSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    try {
      await verifyMfa(mfaCode);
    } catch {
      setLocalError('Invalid code.');
    }
  }

  const busy = status === 'authenticating';
  const shownError = localError ?? error;

  const card = (
    <Card style={{ width: '100%', maxWidth: 400, padding: '2rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <img src="/dpp-logo.png" alt="Dental Purchasing Partners" className="dsp-login-logo" />
        <h1 style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: '1rem' }}>{title}</h1>
        <p className="dsp-muted" style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
          {subtitle}
        </p>
      </div>

      {!showMfa ? (
        <form onSubmit={(e) => void onPasswordSubmit(e)}>
          <Stack gap={14}>
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>
            <div style={{ textAlign: 'right', marginTop: -6 }}>
              <a href={forgotPasswordHref} className="dsp-muted" style={{ fontSize: '0.82rem' }}>
                Forgot password?
              </a>
            </div>
            <Button type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>
        </form>
      ) : (
        <form onSubmit={(e) => void onMfaSubmit(e)}>
          <Stack gap={14}>
            <Field label="Authenticator code">
              <Input
                inputMode="numeric"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                autoComplete="one-time-code"
                required
              />
            </Field>
            <Button type="submit" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify code'}
            </Button>
            <button
              type="button"
              className="dsp-muted"
              style={{
                fontSize: '0.82rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => {
                setShowMfa(false);
                setLocalError(null);
              }}
            >
              Back to password
            </button>
          </Stack>
        </form>
      )}

      {googleConfig && !showMfa ? (
        <>
          <div
            style={{
              textAlign: 'center',
              color: 'var(--dsp-text-muted)',
              margin: '1.1rem 0 0.8rem',
              fontSize: '0.82rem',
            }}
          >
            or
          </div>
          <Button
            type="button"
            variant="secondary"
            style={{ width: '100%' }}
            disabled={busy}
            onClick={() => void onGoogleClick()}
          >
            Continue with Google
          </Button>
        </>
      ) : null}

      {demoAccounts && demoAccounts.length > 0 ? (
        <div style={{ marginTop: '1.1rem' }}>
          <div
            className="dsp-muted"
            style={{ fontSize: '0.72rem', textAlign: 'center', marginBottom: 8 }}
          >
            Demo accounts
          </div>
          <div className="dsp-row" style={{ gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {demoAccounts.map((a) => (
              <button
                key={a.email}
                type="button"
                className="dsp-btn dsp-btn--secondary dsp-btn--sm"
                disabled={busy}
                onClick={() =>
                  void loginWithPassword(a.email, a.password)
                    .then((result) => {
                      if (result === 'mfa_required') setShowMfa(true);
                    })
                    .catch(() => setLocalError('Demo sign-in failed'))
                }
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {shownError ? (
        <p
          style={{
            color: 'var(--dsp-danger-fg)',
            fontSize: '0.85rem',
            marginTop: '1rem',
            textAlign: 'center',
          }}
        >
          {shownError}
        </p>
      ) : null}
    </Card>
  );

  if (embedded) return card;

  return (
    <main
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        padding: '1.5rem',
        background: 'var(--dsp-bg)',
      }}
    >
      {card}
    </main>
  );
}
